// IndexedDB cache for the map's binary point payload (~35MB raw / ~22MB gzip).
// Shared by MapPage (reads on load) and the background prefetcher (warms it
// after login so the map opens instantly).
// ---------------------------------------------------------------------------
const IDB_NAME = 'stonks-map-cache'
const IDB_STORE = 'points'
const IDB_KEY = 'bin'
const CACHE_MAX_AGE = 30 * 24 * 60 * 60 * 1000 // 30 days (MapPage refreshes in the background)

export interface BinaryCacheEntry { ts: number; buf: ArrayBuffer; version?: string }

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 5) // v5: new archipelago clustering positions (build_music_map.py)
    req.onupgradeneeded = () => {
      const db = req.result
      if (db.objectStoreNames.contains(IDB_STORE)) db.deleteObjectStore(IDB_STORE)
      db.createObjectStore(IDB_STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function getCachedBinary(): Promise<BinaryCacheEntry | null> {
  try {
    const db = await openIDB()
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly')
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY)
      req.onsuccess = () => {
        const val = req.result as BinaryCacheEntry | undefined
        if (val && Date.now() - val.ts < CACHE_MAX_AGE) resolve(val)
        else resolve(null)
      }
      req.onerror = () => resolve(null)
    })
  } catch { return null }
}

export async function setCachedBinary(buf: ArrayBuffer, version?: string) {
  try {
    const db = await openIDB()
    const tx = db.transaction(IDB_STORE, 'readwrite')
    tx.objectStore(IDB_STORE).put({ ts: Date.now(), buf, version } as BinaryCacheEntry, IDB_KEY)
  } catch { /* silent */ }
}

let prefetchInFlight = false

/**
 * Warm the IndexedDB map cache in the background so the map page opens
 * instantly. No-op if a fresh (<1h) copy already exists, so it downloads at
 * most once per hour per browser. The payload is served gzipped (~22MB).
 */
export async function prefetchMapBinary(): Promise<void> {
  if (prefetchInFlight) return
  prefetchInFlight = true
  try {
    if (await getCachedBinary()) return // already warm
    const res = await fetch('/api/releases/map/points-bin', { priority: 'low' } as RequestInit & { priority: 'low' })
    if (!res.ok || res.status === 204) return
    const buf = await res.arrayBuffer()
    if (buf.byteLength > 0) await setCachedBinary(buf)
  } catch { /* silent — map page will load normally */ }
  finally { prefetchInFlight = false }
}
